import {
  Button,
  Group,
  Box,
  useMantineTheme,
  Loader,
  ThemeIcon,
} from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import {
  CreateEmployeeInput,
  useCreateEmployeeMutation,
  useGetEmployeeRoleNamesPossibleQuery,
  useGetEmployeeRoleNamesQuery,
  useGetIsUsernameAvailableLazyQuery,
  useGetLocationsQuery,
} from "src/generated/graphql";
import { memo, useState } from "react";
import TextInput from "src/components/TextInput/TextInput";
import { string, object, array } from "yup";
import MultiSelect from "src/components/MultiSelect";
import ImageUpload from "src/components/ImageUpload";
import { AiFillCheckCircle } from "react-icons/ai";
import Select from "src/components/Select";
import { useAppContext } from "src/api/AppContext";

type CreateEmployeeFormProps = {
  handleClose: () => void;
};

export const employeeFormSchema = object().shape({
  staffId: string()
    .nullable()
    .matches(/^\S*$/, "must not contain spaces")
    .max(20, "Staff ID must not be greater than 20 letters"),
  firstName: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "First Name should must be least 2 letters")
    .max(30, "First Name must not be greater than 30 letters")
    .required("First Name is required"),
  middleName: string()
    .nullable()
    .trim("No spaces allowed")
    .strict(true)
    .matches(/.{2,}/, {
      excludeEmptyString: true,
      message: "Middle Name must be at least 2 characters",
    })
    .max(30, "Middle Name must not be greater than 30 letters"),
  lastName: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Last Name should must be least 2 letters")
    .max(30, "Last Name must not be greater than 30 letters")
    .required("Last Name is required"),
  email: string().email().required("Email is required").max(50),
  phoneNumber: string()
    .matches(/^(?:\+\d{9,11}|\d{10,11})?$/, {
      excludeEmptyString: true,
      message: "Enter a valid phone number",
    })
    .notRequired(),
  locations: array()
    .min(1, "at least 1 location is required")
    .required("Location is required"),
  pin: string().nullable().min(4).max(4),
  username: string()
    .trim("no spaces allowed")
    .strict(true)
    .min(4, "Username must be at least 4 characters")
    .max(30, "Username cannot be more than 30 characters")
    .required("Username is required!"),
  role: string().required("Role is required"),
});

export const empInitialValues = {
  username: "",
  staffId: "",
  firstName: "",
  middleName: "",
  lastName: "",
  phoneNumber: "",
  email: "",
  profilePicUrl: "",
  locations: [],
  role: "",
};

type IFormProps = Omit<CreateEmployeeInput, "locations" | "role"> & {
  locations: string[];
  role: string;
};

const CreateEmployeeForm = ({ handleClose }: CreateEmployeeFormProps) => {
  const theme = useMantineTheme();
  const { currentAdmin, currentEmployee } = useAppContext();
  const { data: locArr } = useGetLocationsQuery();
  const { data: empRoleNames } = useGetEmployeeRoleNamesQuery({
    skip: !currentAdmin,
  });

  const { data: empRolePossibleNames } = useGetEmployeeRoleNamesPossibleQuery({
    skip: !currentEmployee,
  });

  const [createEmp, { loading }] = useCreateEmployeeMutation({
    onCompleted: () => {
      handleClose();
      window.location.reload();
    },
  });

  const [isUsernameAvailable, setIsUsernameAvailable] = useState<
    boolean | undefined
  >(undefined);

  const form = useForm({
    initialValues: empInitialValues,

    validate: yupResolver(employeeFormSchema),
  });

  const createEmployee = async (values: IFormProps) => {
    createEmp({
      variables: {
        data: {
          ...values,
          locations: values.locations.map((e) => ({ id: e })),
          role: { id: values.role },
        },
      },
    });
  };

  const [checkUsername, { data: usernameData, loading: isUsernameLoading }] =
    useGetIsUsernameAvailableLazyQuery();

  const checkUsernameAvailability = async (username: string) => {
    if (username.length > 3 && username.length < 31) {
      await checkUsername({
        variables: {
          username,
        },
        onCompleted: (resp) => {
          setIsUsernameAvailable(resp.checkUsernameAvailability);
        },
        fetchPolicy: "no-cache",
      });
    } else {
      setIsUsernameAvailable(undefined);
    }
  };

  return (
    <Box sx={{ maxWidth: 360 }} mx="auto">
      <form
        onSubmit={form.onSubmit((values) => {
          createEmployee({
            ...values,
            username: values.username.toLowerCase(),
          });
        })}
      >
        <ImageUpload
          onReadSuccess={(b64) => form.setFieldValue("profilePicUrl", b64)}
          src={form.values.profilePicUrl || ""}
          alt="Emp Image"
          sx={{
            maxHeight: 200,
            maxWidth: 200,
            margin: "auto",
          }}
        />

        <TextInput
          withAsterisk
          label="Username"
          rightSection={
            isUsernameLoading ? (
              <Loader size="xs" />
            ) : isUsernameAvailable === true ? (
              <ThemeIcon color="green" size={18} radius="xl">
                <AiFillCheckCircle />
              </ThemeIcon>
            ) : undefined
          }
          {...form.getInputProps("username")}
          onBlur={() => {
            checkUsernameAvailability(form.values.username);
            form.getInputProps("username").onBlur();
          }}
          error={
            isUsernameAvailable === false
              ? "Username is taken!"
              : form.errors["username"]
          }
        />
        <TextInput label="Staff ID" {...form.getInputProps("staffId")} />
        <TextInput
          withAsterisk
          label="First Name"
          {...form.getInputProps("firstName")}
        />
        <TextInput label="Middle Name" {...form.getInputProps("middleName")} />
        <TextInput
          withAsterisk
          label="Last Name"
          {...form.getInputProps("lastName")}
        />
        <TextInput
          withAsterisk
          label="Email"
          {...form.getInputProps("email")}
        />
        <TextInput
          label="Phone Number"
          {...form.getInputProps("phoneNumber")}
        />

        <Select
          data={
            (currentEmployee
              ? empRolePossibleNames?.employeeRolesPossible
              : empRoleNames?.employeeRoleMany
            )?.map((val) => ({
              value: val.id,
              label: val.name,
            })) || []
          }
          label="Select role"
          {...form.getInputProps("role")}
        />

        <MultiSelect
          data={
            locArr?.locationMany?.map(
              (val) =>
                ({
                  value: val.id,
                  label: val.name,
                } || [])
            ) || []
          }
          label="Select Locations"
          {...form.getInputProps("locations")}
        />

        <Group position="apart" mt="md" style={{ marginTop: 30 }}>
          <Button
            radius="md"
            variant="outline"
            onClick={handleClose}
            style={{
              flex: 1,
              color: theme.colors.secondaryTextColor[6],
            }}
            sx={{
              backgroundColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.secondary[5],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            radius="md"
            type="submit"
            disabled={loading}
            style={{
              flex: 1,
            }}
            sx={{
              color: theme.colors.primaryTextColor[6],
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
            loading={loading}
          >
            Create
          </Button>
        </Group>
      </form>
    </Box>
  );
};

export default memo(CreateEmployeeForm);
